import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format, isPast } from "date-fns";
import { Bell, Trash2, Calendar, Clock, FileText, BellOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { getStoredReminders, cancelScriptReminder, clearExpiredReminders } from "@/lib/pushNotifications";
import { toast } from "sonner";

interface Reminder {
  id: number;
  scriptId: string;
  scriptTitle: string;
  reminderTime: string;
}

const Reminders = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState<Reminder[]>([]);

  const loadReminders = () => {
    clearExpiredReminders();
    const stored = getStoredReminders();
    // Sort by reminder time, soonest first
    const sorted = stored.sort(
      (a, b) => new Date(a.reminderTime).getTime() - new Date(b.reminderTime).getTime()
    );
    setReminders(sorted);
  };

  useEffect(() => {
    loadReminders();
  }, []);

  const handleCancelReminder = (reminder: Reminder) => {
    cancelScriptReminder(reminder.id);
    loadReminders();
    toast.success(t("reminders.canceled"));
  };

  const handleCancelAll = () => {
    reminders.forEach((r) => cancelScriptReminder(r.id));
    loadReminders();
    toast.success(t("reminders.allCanceled"));
  };

  const isExpired = (time: string) => isPast(new Date(time));

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {t("reminders.title")}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("reminders.subtitle")}
          </p>
        </div>
        {reminders.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="text-destructive">
                {t("reminders.cancelAll")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("reminders.cancelAllConfirm.title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("reminders.cancelAllConfirm.description")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("reminders.cancelAllConfirm.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={handleCancelAll}>
                  {t("reminders.cancelAllConfirm.confirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {/* Reminders List */}
      {reminders.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-400 shadow-none bg-transparent">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <BellOff className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-2">{t("reminders.empty.title")}</h3>
            <p className="text-muted-foreground max-w-sm mb-6">
              {t("reminders.empty.description")}
            </p>
            <Button onClick={() => navigate("/dashboard")}>
              {t("reminders.empty.cta")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reminders.map((reminder) => {
            const expired = isExpired(reminder.reminderTime);
            const reminderDate = new Date(reminder.reminderTime);

            return (
              <Card
                key={reminder.id}
                className={`border-2 border-black shadow-[4px_4px_0_#1A1A1A] transition-all hover:translate-y-[-2px] ${
                  expired ? "opacity-60 border-dashed shadow-none" : ""
                }`}
              >
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Bell className="w-6 h-6 text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{reminder.scriptTitle}</h3>
                      {expired && (
                        <Badge variant="secondary" className="text-xs">
                          {t("reminders.expired")}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {format(reminderDate, "MMM d, yyyy")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {format(reminderDate, "h:mm a")}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/editor/${reminder.scriptId}`)}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      {t("reminders.viewScript")}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="icon">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#F5F0E8] border-2 border-black shadow-[8px_8px_0_#1A1A1A]">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("reminders.cancelConfirm.title")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("reminders.cancelConfirm.description", {
                              title: reminder.scriptTitle,
                            })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("reminders.cancelConfirm.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleCancelReminder(reminder)}>
                            {t("reminders.cancelConfirm.confirm")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Reminders;
